// ============================================================
// PlatformBillingPage.js — Platform-10 admin. The platform-DEFAULT rate card
// editor + the dry-run PREVIEW across all billing-enabled clients.
// (Per-client overrides live on the client's Billing tab.)
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRateCard, setRatePrice, getBillingPreview, poundsFromPence, penceFromPounds } from '../api/billing';
import { toast } from '../components/Toaster';
import StatusMessage from '../components/StatusMessage';
import BcStatesTable from '../components/BcStatesTable';
import './ClientBillingTab.css';

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function PlatformBillingPage() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [period, setPeriod] = useState(thisMonth());
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const loadCard = useCallback(async () => {
    setLoading(true);
    try {
      const card = await getRateCard();
      setItems(card.items || []);
      const d = {}; (card.items || []).forEach((i) => { d[i.ItemKey] = poundsFromPence(i.PricePence); });
      setDrafts(d);
    } catch (err) { toast.error(err?.detail || err?.message || 'Failed to load the rate card.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCard(); }, [loadCard]);

  async function savePrice(itemKey) {
    const pence = penceFromPounds(drafts[itemKey]);
    if (pence == null) { toast.error('Enter a valid price (e.g. 100.00).'); return; }
    setBusy(true);
    try {
      await setRatePrice(itemKey, { pricePence: pence });
      toast.success('Price updated.');
      await loadCard();
    } catch (err) { toast.error(err?.detail || err?.message || 'Could not update.'); }
    finally { setBusy(false); }
  }

  async function runPreview() {
    if (!/^\d{4}-\d{2}$/.test(period)) { toast.error('Period must be YYYY-MM.'); return; }
    setPreviewing(true); setPreview(null);
    try { setPreview(await getBillingPreview(period)); }
    catch (err) { toast.error(err?.detail || err?.message || 'Preview failed.'); }
    finally { setPreviewing(false); }
  }

  const products = items.filter((i) => i.ItemType === 'product');
  const seats = items.filter((i) => i.ItemType === 'seat');

  return (
    <div className="cbill" style={{ maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ margin: '0.5rem 0' }}>Billing</h1>

      {/* Rate card */}
      <div className="cbill__card">
        <h3 className="cbill__title">Platform default prices</h3>
        <p className="cbill__hint">The standard prices for every client. Override any of these per customer on that client&apos;s <strong>Billing</strong> tab. A change takes effect from today; past runs keep their prices.</p>
        {loading ? <StatusMessage variant="loading" className="cbill__state">Loading…</StatusMessage> : (
          <table className="cbill__table">
            <thead><tr><th>Item</th><th className="cbill__num">Price (£)</th><th /></tr></thead>
            <tbody>
              {[['Products (module base)', products], ['Seats (per user)', seats]].map(([heading, group]) => (
                <React.Fragment key={heading}>
                  <tr><td colSpan={3} className="cbill__muted" style={{ paddingTop: 12 }}>{heading}</td></tr>
                  {group.map((it) => (
                    <tr key={it.ItemKey}>
                      <td>{it.Description}</td>
                      <td className="cbill__num">
                        <input className="cbill__input" type="text" inputMode="decimal" value={drafts[it.ItemKey] ?? ''} disabled={busy}
                          onChange={(e) => setDrafts((d) => ({ ...d, [it.ItemKey]: e.target.value }))} />
                      </td>
                      <td className="cbill__actions"><button className="button-secondary" disabled={busy} onClick={() => savePrice(it.ItemKey)}>Save</button></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview */}
      <div className="cbill__card">
        <div className="cbill__row">
          <div>
            <h3 className="cbill__title">Monthly preview (dry-run)</h3>
            <p className="cbill__hint">Every billing-enabled client&apos;s computed bill. Nothing is sent — this is a preview only.</p>
          </div>
          <div className="cbill__actions">
            <input className="cbill__input" style={{ width: 110 }} type="text" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM" />
            <button className="button-primary" disabled={previewing} onClick={runPreview}>{previewing ? 'Running…' : 'Run preview'}</button>
          </div>
        </div>

        {preview && (
          <>
            <table className="cbill__table">
              <thead><tr><th>Client</th><th className="cbill__num">Products</th><th className="cbill__num">Seats</th><th className="cbill__num">Total</th></tr></thead>
              <tbody>
                {preview.bills.length === 0 && <tr><td colSpan={4} className="cbill__muted">No billing-enabled clients.</td></tr>}
                {preview.bills.map((b) => {
                  const prod = b.lines.filter((l) => l.itemType === 'product').reduce((s, l) => s + l.amountPence, 0);
                  const seat = b.lines.filter((l) => l.itemType === 'seat').reduce((s, l) => s + l.amountPence, 0);
                  return (
                    <tr key={b.clientId}>
                      <td><Link to={`/clients/${b.clientId}?tab=billing`}>{b.clientName}</Link></td>
                      <td className="cbill__num">£{poundsFromPence(prod)}</td>
                      <td className="cbill__num">£{poundsFromPence(seat)}</td>
                      <td className="cbill__num" style={{ fontWeight: 700 }}>£{poundsFromPence(b.totalPence)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr><td colSpan={3} className="cbill__num cbill__total">Grand total ({preview.period})</td><td className="cbill__num cbill__total">£{poundsFromPence(preview.grandTotalPence)}</td></tr></tfoot>
            </table>
          </>
        )}
      </div>

      <BcStatesTable />
    </div>
  );
}
